"use client";

import React, { useState } from "react";
import { ShieldAlert, Award, Calendar, Info, Clock } from "lucide-react";
import { fetchWithAuth, API_BASE } from "@/lib/auth";

interface Assignment {
    id: string;
    gig_posting_id: string;
    gig_posting_title: string;
    owning_entity_type: string;
    owning_entity_id: number;
    owning_entity_name?: string;
    volunteer_id: string;
    volunteer_name: string;
    volunteer_email: string;
    origin: string;
    status: string;
    assigned_at: string | null;
    started_at: string | null;
    completed_at: string | null;
    recognition_tier: string;
}

interface ConsoleAssignmentTableProps {
    assignments: Assignment[];
    isLoading: boolean;
    onSelectVolunteer?: (id: string) => void;
    onActionSuccess?: () => void;
}

// ── helpers ──────────────────────────────────────────────────────────────────

function getStatusStyle(status: string): string {
    const s = status.toLowerCase();
    if (s === "active" || s === "assigned" || s === "in_progress") return "bg-[#cbeef2]/40 text-[#007a8a] border-[#007a8a]/20";
    if (s === "completed")                                          return "bg-[#a9c4bb]/20 text-[#004d57] border-[#a9c4bb]/40";
    if (s === "cancelled" || s === "removed" || s === "withdrawn") return "bg-red-50 text-red-700 border-red-100";
    return "bg-gray-50 text-gray-600 border-gray-200";
}

function getRecognitionStyle(tier: string): string {
    const t = tier.toLowerCase();
    if (t === "tier_a" || t === "tiera" || t === "tier1") return "bg-amber-50 text-amber-700 border-amber-200";
    if (t === "tier_b" || t === "tierb" || t === "tier2") return "bg-[#007a8a]/5 text-[#007a8a] border-[#007a8a]/20";
    return "bg-gray-50 text-gray-500 border-gray-200";
}

function getOriginStyle(origin: string): string {
    const o = origin.toLowerCase();
    if (o === "invited") return "bg-[#004d57]/5 text-[#004d57] border-[#004d57]/10";
    if (o === "applied") return "bg-[#007a8a]/5 text-[#007a8a] border-[#007a8a]/10";
    return "bg-gray-100/60 text-gray-500 border-gray-200";
}

function getEntityStyle(type: string): string {
    const t = type.toLowerCase();
    if (t === "staff")     return "bg-rose-50 text-rose-700 border-rose-100";
    if (t === "committee") return "bg-[#003d45]/5 text-[#003d45] border-[#003d45]/10";
    if (t === "chapter")   return "bg-[#007a8a]/5 text-[#007a8a] border-[#007a8a]/10";
    if (t === "community") return "bg-[#ae232d]/5 text-[#ae232d] border-[#ae232d]/10";
    return "bg-[#64748b]/5 text-[#64748b] border-[#64748b]/10";
}

function initials(name: string): string {
    const parts = name.trim().split(" ");
    return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : (name[0] || "V").toUpperCase();
}

// ── component ────────────────────────────────────────────────────────────────

export default function ConsoleAssignmentTable({
    assignments,
    isLoading,
    onSelectVolunteer,
    onActionSuccess,
}: ConsoleAssignmentTableProps) {
    const [actioningId, setActioningId]               = useState<string | null>(null);
    const [completingId, setCompletingId]             = useState<string | null>(null);
    const [recognitionOverride, setRecognitionOverride] = useState<string | null>(null);
    const [actionError, setActionError]               = useState<string | null>(null);

    const handleComplete = async (id: string) => {
        setActioningId(id); setActionError(null);
        try {
            const res = await fetchWithAuth(`${API_BASE}/api/v1/gigs/console/assignments/${id}/complete`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ recognition_override: recognitionOverride }),
            });
            if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Failed to complete assignment."); }
            setCompletingId(null); setRecognitionOverride(null);
            if (onActionSuccess) onActionSuccess();
        } catch (err: any) { setActionError(err.message || "Failed to complete assignment."); }
        finally { setActioningId(null); }
    };

    const fmtDate = (dStr: string | null) => {
        if (!dStr) return null;
        return new Date(dStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    };

    // ── loading / empty ───────────────────────────────────────────────────────

    if (isLoading) {
        return (
            <div className="py-12 flex flex-col items-center justify-center gap-3">
                <div className="w-6 h-6 border-2 border-[#007a8a]/20 border-t-[#007a8a] rounded-full animate-spin" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Loading assignments...</p>
            </div>
        );
    }

    if (assignments.length === 0) {
        return (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-150 shadow-sm">
                <ShieldAlert className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                <h3 className="text-sm font-bold text-[#003d45] uppercase tracking-wider">No Assignments</h3>
                <p className="text-xs text-gray-400 mt-1">There are no active or historical assignments in this scope.</p>
            </div>
        );
    }

    const showActions = !!onSelectVolunteer || !!onActionSuccess;

    // ── render ────────────────────────────────────────────────────────────────

    return (
        <div className="space-y-4">
            {actionError && (
                <div className="bg-red-50 text-red-700 p-4 border border-red-100 rounded-2xl text-xs flex items-center gap-2">
                    <Info className="w-4 h-4 text-red-500 shrink-0" />
                    <span><strong>Error:</strong> {actionError}</span>
                </div>
            )}

            <div className="flex flex-col gap-4">
                {assignments.map((a) => {
                    const statusLower = a.status.toLowerCase();
                    const isActive    = statusLower === "assigned" || statusLower === "in_progress" || statusLower === "active";
                    const isDone      = statusLower === "completed";
                    const hasRecognition = a.recognition_tier && a.recognition_tier.toLowerCase() !== "none";

                    const assignedDate  = fmtDate(a.assigned_at);
                    const startedDate   = fmtDate(a.started_at);
                    const completedDate = fmtDate(a.completed_at);

                    return (
                        <div
                            key={a.id}
                            className="bg-white rounded-xl border border-gray-150 p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col md:flex-row md:items-center justify-between gap-6 text-left"
                        >
                            {/* Column 1: Volunteer + assignment details */}
                            <div className="space-y-1.5 flex-1 min-w-0">
                                {/* Name + origin badge */}
                                <div className="flex items-center gap-2.5 flex-wrap">
                                    <div className="w-7 h-7 rounded-full bg-[#4b8892] text-white text-[10px] font-black flex items-center justify-center uppercase shrink-0">
                                        {initials(a.volunteer_name)}
                                    </div>
                                    <button
                                        onClick={() => onSelectVolunteer && onSelectVolunteer(a.volunteer_id)}
                                        className={`text-base font-bold text-[#003d45] text-left focus:outline-none transition-colors leading-tight ${onSelectVolunteer ? "hover:text-[#007a8a] cursor-pointer" : "cursor-default"}`}
                                    >
                                        {a.volunteer_name}
                                    </button>
                                    {a.origin && (
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${getOriginStyle(a.origin)}`}>
                                            {a.origin}
                                        </span>
                                    )}
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border ${getStatusStyle(a.status)}`}>
                                        {a.status.replace(/_/g, " ")}
                                    </span>
                                </div>

                                {/* Gig + entity */}
                                <p className="text-xs text-gray-400 font-medium flex items-center flex-wrap gap-x-1.5">
                                    <span className="text-gray-500 font-semibold">{a.gig_posting_title}</span>
                                    <span className="text-gray-300">·</span>
                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${getEntityStyle(a.owning_entity_type)}`}>
                                        {a.owning_entity_name || `${a.owning_entity_type}: ${a.owning_entity_id}`}
                                    </span>
                                </p>

                                {/* Timeline chips */}
                                <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 pt-0.5">
                                    {assignedDate && (
                                        <span className="flex items-center gap-1 text-[10px] text-gray-400 font-medium">
                                            <Calendar className="w-3 h-3" /> Assigned {assignedDate}
                                        </span>
                                    )}
                                    {startedDate && (
                                        <span className="flex items-center gap-1 text-[10px] text-[#4b8892] font-semibold">
                                            <Clock className="w-3 h-3" /> Started {startedDate}
                                        </span>
                                    )}
                                    {completedDate && (
                                        <span className="flex items-center gap-1 text-[10px] text-[#003d45] font-semibold">
                                            <Calendar className="w-3 h-3" /> Completed {completedDate}
                                        </span>
                                    )}
                                </div>

                                {/* Recognition */}
                                {hasRecognition && (
                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border ${getRecognitionStyle(a.recognition_tier)}`}>
                                        <Award className="w-3 h-3 shrink-0" />
                                        {a.recognition_tier.replace(/_/g, " ")}
                                    </span>
                                )}
                            </div>

                            {/* Column 2: Assignment date stat */}
                            <div className="text-center shrink-0 w-20">
                                <div className="text-lg font-black text-[#003d45]">
                                    {a.assigned_at
                                        ? new Date(a.assigned_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }).split(" ")[1]
                                        : "—"}
                                </div>
                                <div className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mt-0.5">
                                    {a.assigned_at
                                        ? new Date(a.assigned_at).toLocaleDateString("en-US", { month: "short" })
                                        : "Date"}
                                </div>
                            </div>

                            {/* Column 3: Actions */}
                            {showActions && (
                                <div className="flex items-center gap-3 shrink-0 self-end md:self-center">
                                    {isActive && onActionSuccess ? (
                                        completingId === a.id ? (
                                            <div className="space-y-2 text-left w-52">
                                                <label className="text-[9px] font-bold uppercase tracking-wider text-gray-500 block">
                                                    Recognition Override
                                                </label>
                                                <select
                                                    value={recognitionOverride || ""}
                                                    onChange={(e) => setRecognitionOverride(e.target.value || null)}
                                                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-[10px] focus:outline-none focus:border-[#007a8a] text-gray-700 bg-white font-medium"
                                                    disabled={actioningId === a.id}
                                                >
                                                    <option value="">-- Use Default --</option>
                                                    <option value="tier1">Tier 1 (Core Leadership)</option>
                                                    <option value="tier2">Tier 2 (General Contribution)</option>
                                                </select>
                                                <div className="flex gap-2 justify-end">
                                                    <button
                                                        onClick={() => { setCompletingId(null); setRecognitionOverride(null); }}
                                                        className="px-3 py-1.5 rounded border border-gray-200 text-[10px] font-bold uppercase tracking-wider text-gray-500 hover:bg-gray-50 cursor-pointer bg-white"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={() => handleComplete(a.id)}
                                                        className="px-3 py-1.5 bg-[#004d57] hover:bg-[#003c44] rounded text-[10px] font-bold uppercase tracking-wider text-white cursor-pointer border-none shadow-sm shadow-[#004d57]/10"
                                                        disabled={actioningId === a.id}
                                                    >
                                                        {actioningId === a.id ? "Saving..." : "Confirm"}
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => { setCompletingId(a.id); setRecognitionOverride(null); }}
                                                className="bg-[#004d57] text-white rounded-lg px-4 py-2 font-bold text-xs uppercase tracking-wider hover:bg-[#003c44] transition-all border-none cursor-pointer shadow-sm shadow-[#004d57]/10"
                                                disabled={actioningId !== null}
                                            >
                                                Mark complete
                                            </button>
                                        )
                                    ) : null}
                                    {onSelectVolunteer && (
                                        <button
                                            onClick={() => onSelectVolunteer(a.volunteer_id)}
                                            className="text-[#004d57] hover:text-[#003c44] font-semibold text-[11px] cursor-pointer border-none bg-transparent hover:underline uppercase tracking-wider"
                                            disabled={actioningId !== null}
                                        >
                                            Profile
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
